import { z } from 'zod';
// ─── Tool-ref validation warnings (DEV-16944) ────────────────────────────────
/**
 * Save-time advisory produced when an `llm-agent` node's (or Agent row's)
 * `toolRefs` entry resolves to neither a platform tool id from the
 * `@cortex/mcp-tools` registry nor an MCPConnection name visible to the org
 * (org-owned or catalog, not soft-deleted).
 *
 * Warnings NEVER block the save — the runner simply skips unresolvable
 * refs at execution time (see `resolveTools` in `@cortex/node-types`), so
 * the save-time warning is the author's early signal, not a gate.
 *
 * `nodeKey` is set when the warning came from a workflow-graph save (which
 * node carried the bad ref); absent for Agent-row validation.
 */
export const ToolRefWarningSchema = z.object({
    nodeKey: z.string().optional(),
    ref: z.string(),
    kind: z.literal('unknown_tool_ref'),
    message: z.string(),
});
// ─── MCP connection referential safety (DEV-16944) ───────────────────────────
/**
 * One consumer of an MCPConnection *name*: either an Agent row whose
 * `toolRefs` contains the name, or a tools-bearing WorkflowNode (node type
 * whose config declares `toolRefs` — `llm-agent` today) whose
 * `config.toolRefs` contains it. Nodes are scanned across ALL versions of
 * the org's workflows — the same population a `propagateRefs: true` rename
 * rewrites (runs execute from version state, so older versions are real
 * referents) — deduplicated to one referent per (workflowId, nodeKey).
 *
 *  - `kind: 'agent'`         → `id` = Agent id, `name` = agent name.
 *  - `kind: 'workflow-node'` → `id` = a WorkflowNode row id for that node,
 *    `name` = workflow name, with `workflowId` + `nodeKey` locating the node.
 */
export const McpConnectionReferentSchema = z.object({
    kind: z.enum(['agent', 'workflow-node']),
    id: z.string(),
    name: z.string(),
    workflowId: z.string().optional(),
    nodeKey: z.string().optional(),
});
/**
 * 409 payload for `PATCH /v1/mcp-connections/:id` (and `/:id/metadata`) when
 * the rename would orphan existing tool refs: the connection's current name
 * is still referenced by at least one agent or workflow node. Either update
 * the referents first (or keep the name), or retry with
 * `propagateRefs: true` to rename AND rewrite every reference atomically —
 * `hint` spells out that recovery path for API consumers.
 */
export const McpConnectionNameInUseErrorSchema = z.object({
    code: z.literal('CONNECTION_NAME_IN_USE'),
    message: z.string(),
    referents: z.array(McpConnectionReferentSchema),
    /**
     * Machine-relayable recovery hint ("retry with propagateRefs: true …").
     * Optional in the schema for backward compatibility with payloads minted
     * before DEV-16944 propagation shipped; the API always sends it now.
     */
    hint: z.string().optional(),
});
/**
 * Count summary returned by `PATCH /v1/mcp-connections/:id[/metadata]` when a
 * rename ran with `propagateRefs: true`. Each field is the number of rows
 * whose stored references were rewritten from the old connection name to the
 * new one, all inside the same transaction as the rename itself:
 *
 *  - `agents`           → Agent rows (`toolRefs` entries + `toolAllowlists`
 *                          keys).
 *  - `workflowNodes`    → tools-bearing WorkflowNode rows (any version) whose
 *                          `config.toolRefs` carried the old name.
 *  - `workflowVersions` → WorkflowVersion rows whose `graph` snapshot carried
 *                          the old name in a tools-bearing node config. Runs
 *                          execute from version graphs, so published versions
 *                          are rewritten too — not just current-version rows.
 *
 * Persistent-agent threads need no rewrite: they hold no build-time copy of
 * `Agent.toolRefs`. Every thread resolves its tools live from the base agent
 * at build time, and `reset` is the way to rebuild one.
 */
export const McpConnectionRenamePropagationSchema = z.object({
    agents: z.number().int().nonnegative(),
    workflowNodes: z.number().int().nonnegative(),
    workflowVersions: z.number().int().nonnegative(),
});
// ─── Platform tool enumeration (DEV-16944) ───────────────────────────────────
/**
 * One built-in platform tool from the `@cortex/mcp-tools` registry — the
 * registry's default profile, i.e. exactly the set the runtime executor
 * resolves `toolRefs` against (experimental tools are excluded on both
 * sides, so nothing enumerated here can fail to resolve at run time).
 * These are the ids an Agent / `llm-agent` node may carry in `toolRefs`
 * alongside MCP connection names. `group` is Formant's tool-group taxonomy
 * (kg / eval / workflow / …) when the tool declares one.
 */
export const PlatformToolSchema = z.object({
    id: z.string(),
    description: z.string(),
    group: z.string().optional(),
});
/**
 * Response body for `GET /v1/platform-tools`. The registry is code, not
 * data — the list is static for the process lifetime and identical for
 * every org.
 */
export const ListPlatformToolsResponseSchema = z.object({
    items: z.array(PlatformToolSchema),
});
/**
 * One warning in the `DELETE /v1/mcp-connections/:id` response: the
 * soft-deleted connection is still referenced by `referent`. The delete is
 * NOT blocked (soft-delete is reversible and the runner tolerates
 * unresolvable refs) — the warning list lets the console prompt the user
 * to clean up the referents.
 */
export const McpConnectionDeletedWarningSchema = z.object({
    kind: z.literal('connection_deleted_in_use'),
    /** The deleted connection's name (what the referents' toolRefs carry). */
    ref: z.string(),
    message: z.string(),
    referent: McpConnectionReferentSchema,
});
/**
 * Response body for `DELETE /v1/mcp-connections/:id`. `warnings` is empty
 * when nothing references the deleted connection.
 */
export const DeleteMcpConnectionResponseSchema = z.object({
    warnings: z.array(McpConnectionDeletedWarningSchema),
});
//# sourceMappingURL=tool-ref.js.map